import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import axios from "axios";
import { 
  Calendar, 
  Save, 
  Loader2, 
  Plus,
  Trash2,
  User,
  MessageSquare,
  RefreshCw,
  X,
  Users,
  Edit2,
  Check
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WorkSchedule() {
  const [scheduleData, setScheduleData] = useState({
    days: {},
    dayColors: {},
    comments: ""
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showColorPicker, setShowColorPicker] = useState(null);
  const [newPerson, setNewPerson] = useState("");
  const [newComment, setNewComment] = useState("");
  const [editingPerson, setEditingPerson] = useState(null);
  const [editName, setEditName] = useState("");
  const [editComment, setEditComment] = useState("");
  const colorPickerRef = useRef(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  const fetchSchedule = async () => {
    try {
      const response = await axios.get(`${API}/admin/schedule`, {
        headers: getAuthHeaders()
      });
      if (response.data) {
        setScheduleData({
          days: response.data.days || {},
          dayColors: response.data.dayColors || {},
          comments: response.data.comments || ""
        });
      }
    } catch (error) {
      console.error("Error fetching schedule:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSchedule();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(e.target)) {
        setShowColorPicker(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.post(`${API}/admin/schedule`, scheduleData, {
        headers: getAuthHeaders()
      });
      toast.success("График сохранён!");
    } catch (error) {
      console.error("Error saving schedule:", error);
      toast.error("Ошибка сохранения");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDayClick = (day, e) => {
    e.stopPropagation();
    // Сразу открываем панель сотрудников при клике
    setSelectedDay(day);
    setShowColorPicker(null);
  };

  const handleDayRightClick = (day, e) => {
    e.preventDefault();
    e.stopPropagation();
    // Правый клик - показать выбор цвета
    if (showColorPicker === day) {
      setShowColorPicker(null);
    } else {
      setShowColorPicker(day);
    }
  };

  const setDayColor = (day, color) => {
    setScheduleData(prev => ({
      ...prev,
      dayColors: {
        ...prev.dayColors,
        [String(day)]: color
      }
    }));
    setShowColorPicker(null);
  };

  const openDayPanel = (day) => {
    setSelectedDay(day);
    setShowColorPicker(null);
  };

  const addPerson = () => {
    if (!selectedDay || !newPerson.trim()) {
      toast.error("Введите ФИО");
      return;
    }
    
    const dayKey = String(selectedDay);
    const currentPeople = scheduleData.days[dayKey] || [];
    
    setScheduleData(prev => ({
      ...prev,
      days: {
        ...prev.days,
        [dayKey]: [...currentPeople, { 
          name: newPerson.trim(), 
          comment: newComment.trim(),
          id: Date.now() 
        }]
      }
    }));
    
    setNewPerson("");
    setNewComment("");
  };

  const removePerson = (day, personId) => {
    const dayKey = String(day);
    setScheduleData(prev => ({
      ...prev,
      days: {
        ...prev.days,
        [dayKey]: (prev.days[dayKey] || []).filter(p => p.id !== personId)
      }
    }));
  };

  const startEditPerson = (person) => {
    setEditingPerson(person.id);
    setEditName(person.name);
    setEditComment(person.comment || "");
  };

  const saveEditPerson = () => {
    if (!editingPerson || !editName.trim()) return;
    
    const dayKey = String(selectedDay);
    setScheduleData(prev => ({
      ...prev,
      days: {
        ...prev.days,
        [dayKey]: (prev.days[dayKey] || []).map(p => 
          p.id === editingPerson 
            ? { ...p, name: editName.trim(), comment: editComment.trim() }
            : p
        )
      }
    }));
    
    setEditingPerson(null);
    setEditName("");
    setEditComment("");
  };

  const cancelEdit = () => {
    setEditingPerson(null);
    setEditName("");
    setEditComment("");
  };

  const getDayPeopleCount = (day) => {
    return (scheduleData.days[String(day)] || []).length;
  };

  const getDayColor = (day) => {
    return scheduleData.dayColors[String(day)] || null;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const days = Array.from({ length: 31 }, (_, i) => i + 1);

  return (
    <div className="animate-fade-in" data-testid="work-schedule-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-lg flex items-center justify-center shadow">
              <Calendar className="w-4 h-4 text-white" />
            </div>
            Рабочий график
          </h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchSchedule}>
            <RefreshCw className="w-3 h-3 mr-1" />
            Обновить
          </Button>
          <Button 
            size="sm"
            onClick={handleSave} 
            disabled={isSaving}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isSaving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
            Сохранить
          </Button>
        </div>
      </div>

      {/* Calendar Days - Compact Row */}
      <Card className="mb-4">
        <CardContent className="py-3 px-4">
          <div className="flex flex-wrap gap-1">
            {days.map(day => {
              const count = getDayPeopleCount(day);
              const color = getDayColor(day);
              const isSelected = selectedDay === day;
              
              let bgClass = 'bg-slate-100 hover:bg-slate-200';
              let textClass = 'text-slate-600';
              
              if (color === 'green') {
                bgClass = 'bg-green-500 hover:bg-green-600';
                textClass = 'text-white';
              } else if (color === 'red') {
                bgClass = 'bg-red-500 hover:bg-red-600';
                textClass = 'text-white';
              } else if (count > 0) {
                bgClass = 'bg-blue-100 hover:bg-blue-200';
                textClass = 'text-blue-700';
              }
              
              if (isSelected) {
                bgClass = 'bg-blue-600 hover:bg-blue-700 ring-2 ring-blue-300';
                textClass = 'text-white';
              }
              
              return (
                <div key={day} className="relative">
                  <button
                    onClick={(e) => handleDayClick(day, e)}
                    onContextMenu={(e) => handleDayRightClick(day, e)}
                    className={`w-8 h-8 rounded text-xs font-medium transition-all ${bgClass} ${textClass}`}
                    title="Клик - сотрудники, ПКМ - цвет"
                  >
                    {day}
                    {count > 0 && !color && (
                      <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-blue-500 text-white rounded-full text-[9px] flex items-center justify-center font-bold">
                        {count}
                      </span>
                    )}
                  </button>
                  
                  {/* Color Picker Popup */}
                  {showColorPicker === day && (
                    <div 
                      ref={colorPickerRef}
                      className="absolute top-full left-0 mt-1 z-50 bg-white rounded-lg shadow-xl border p-2 min-w-[120px]"
                    >
                      <div className="flex gap-1 mb-2">
                        <button
                          onClick={() => setDayColor(day, 'green')}
                          className="w-8 h-8 bg-green-500 hover:bg-green-600 rounded flex items-center justify-center"
                          title="Зелёный"
                        >
                          {color === 'green' && <span className="text-white text-xs">✓</span>}
                        </button>
                        <button
                          onClick={() => setDayColor(day, 'red')}
                          className="w-8 h-8 bg-red-500 hover:bg-red-600 rounded flex items-center justify-center"
                          title="Красный"
                        >
                          {color === 'red' && <span className="text-white text-xs">✓</span>}
                        </button>
                        <button
                          onClick={() => setDayColor(day, null)}
                          className="w-8 h-8 bg-slate-200 hover:bg-slate-300 rounded flex items-center justify-center text-xs"
                          title="Сбросить"
                        >
                          ✕
                        </button>
                      </div>
                      <button
                        onClick={() => openDayPanel(day)}
                        className="w-full text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 rounded py-1.5 flex items-center justify-center gap-1"
                      >
                        <Users className="w-3 h-3" />
                        Сотрудники {count > 0 && `(${count})`}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex gap-3 mt-2 text-[10px] text-slate-400">
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-green-500 rounded"></span>Зелёный</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-red-500 rounded"></span>Красный</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-blue-100 rounded"></span>Есть сотрудники</span>
          </div>
        </CardContent>
      </Card>

      {/* Selected Day Panel - Compact */}
      {selectedDay && (
        <Card className="mb-4 border-2 border-blue-200">
          <CardHeader className="py-2 px-4 bg-blue-50">
            <div className="flex items-center justify-between">
              <span className="font-medium text-blue-800 text-sm flex items-center gap-2">
                <Users className="w-4 h-4" />
                {selectedDay} число
              </span>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setSelectedDay(null)}>
                <X className="w-3 h-3" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="py-3 px-4">
            {/* Compact Add Form */}
            <div className="flex gap-2 mb-3">
              <Input
                value={newPerson}
                onChange={(e) => setNewPerson(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addPerson()}
                placeholder="ФИО"
                className="h-8 text-sm flex-1"
              />
              <Input
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addPerson()}
                placeholder="Комментарий"
                className="h-8 text-sm flex-1"
              />
              <Button size="sm" onClick={addPerson} className="h-8 px-3">
                <Plus className="w-3 h-3" />
              </Button>
            </div>

            {/* Compact People List */}
            <div className="space-y-1 overflow-y-auto">
              {(scheduleData.days[String(selectedDay)] || []).length === 0 ? (
                <div className="text-center py-4 text-slate-400 text-sm">
                  <User className="w-6 h-6 mx-auto mb-1 opacity-50" />
                  Нет сотрудников
                </div>
              ) : (
                (scheduleData.days[String(selectedDay)] || []).map((person, index) => (
                  <div 
                    key={person.id} 
                    className="py-1.5 px-2 bg-slate-50 rounded text-sm group hover:bg-slate-100"
                  >
                    {editingPerson === person.id ? (
                      /* Режим редактирования */
                      <div className="space-y-1">
                        <div className="flex gap-1">
                          <Input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            placeholder="ФИО"
                            className="h-7 text-sm flex-1"
                            autoFocus
                          />
                          <Button size="icon" variant="ghost" onClick={saveEditPerson} className="h-7 w-7 text-green-600">
                            <Check className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={cancelEdit} className="h-7 w-7 text-red-500">
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                        <Input
                          value={editComment}
                          onChange={(e) => setEditComment(e.target.value)}
                          placeholder="Комментарий"
                          className="h-7 text-sm"
                          onKeyPress={(e) => e.key === 'Enter' && saveEditPerson()}
                        />
                      </div>
                    ) : (
                      /* Режим просмотра */
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center text-white text-[10px] flex-shrink-0">
                            {index + 1}
                          </span>
                          <div className="min-w-0">
                            <span className="font-medium text-slate-700 truncate block">{person.name}</span>
                            {person.comment && (
                              <span className="text-[10px] text-slate-400 truncate block">💬 {person.comment}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => startEditPerson(person)}
                            className="h-6 w-6 opacity-0 group-hover:opacity-100 text-blue-500 hover:text-blue-600 flex-shrink-0"
                          >
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removePerson(selectedDay, person.id)}
                            className="h-6 w-6 opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-600 flex-shrink-0"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Comments - Compact */}
      <Card>
        <CardHeader className="py-2 px-4">
          <CardTitle className="flex items-center gap-2 text-sm">
            <MessageSquare className="w-4 h-4 text-amber-500" />
            Комментарии
          </CardTitle>
        </CardHeader>
        <CardContent className="py-2 px-4">
          <Textarea
            value={scheduleData.comments}
            onChange={(e) => setScheduleData(prev => ({ ...prev, comments: e.target.value }))}
            placeholder="Заметки..."
            rows={2}
            className="resize-none text-sm"
          />
        </CardContent>
      </Card>
    </div>
  );
}
